import React, { useState, useEffect } from 'react';
import { Bell, X, Gift, Award, Clock, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const LoyaltyNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get user email from localStorage (set during booking)
  const userEmail = localStorage.getItem('user_email');

  useEffect(() => {
    if (userEmail) {
      fetchNotifications();
      // Check for new notifications every 5 minutes
      const interval = setInterval(fetchNotifications, 300000);
      return () => clearInterval(interval);
    }
  }, [userEmail]);

  const fetchNotifications = async () => {
    if (!userEmail) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API}/loyalty-notifications/${userEmail}`);
      setNotifications(response.data);
      
      // Count unread
      const unread = response.data.filter(n => !n.read).length;
      setUnreadCount(unread);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/loyalty-notifications/mark-read/${notificationId}`);
      
      // Update local state
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'milestone':
        return <Award className="text-yellow-400" size={20} />;
      case 'credits_earned':
        return <Gift className="text-purple-400" size={20} />;
      case 'reminder':
        return <Clock className="text-blue-400" size={20} />;
      default:
        return <Sparkles className="text-pink-400" size={20} />;
    }
  };

  if (!userEmail) return null;

  return (
    <>
      {/* Notification Bell Button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-[9998] bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform"
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Panel */}
      {showPanel && (
        <div className="fixed bottom-44 right-4 md:bottom-24 md:right-6 z-[9998] w-96 max-w-[calc(100vw-2rem)] max-h-[600px] bg-gradient-to-br from-purple-900/95 to-black/95 backdrop-blur-lg border border-purple-500/30 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bell size={20} className="text-white" />
              <h3 className="text-white font-bold">Loyalty Rewards</h3>
            </div>
            <button
              onClick={() => setShowPanel(false)}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[500px] p-4 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles size={48} className="mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">No notifications yet</p>
                <p className="text-gray-500 text-sm mt-2">Keep booking to earn rewards!</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`bg-black/40 border rounded-lg p-4 cursor-pointer transition-all hover:bg-black/60 ${
                    !notification.read
                      ? 'border-purple-500/50 shadow-lg shadow-purple-500/20'
                      : 'border-purple-500/20'
                  }`}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getIcon(notification.notification_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="text-white font-semibold text-sm">
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <span className="flex-shrink-0 w-2 h-2 bg-purple-400 rounded-full ml-2 mt-1 animate-pulse"></span>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {notification.message}
                      </p>
                      <p className="text-gray-500 text-xs mt-2">
                        {new Date(notification.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="bg-black/40 border-t border-purple-500/20 px-6 py-3">
              <p className="text-gray-400 text-xs text-center">
                {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up! 🎉'}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default LoyaltyNotifications;
